// src/pages/EmployeeList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import jobTitlesData from "../config/jobTitles.json";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import "../styles/EmployeeList.css";

import Axios from "../utils/Axios";
import Toast from "../utils/Toast";
import AuthHandler from "../utils/AuthHandler";
import validateForm from "../utils/Validation";
import AccessRestricted from "../components/AccessRestricted";

import { FETCH_EMPLOYEES_REQUEST } from "../store/Actions/Actions";
import {
  ADD_EMPLOYEE,
  EMPLOYEE_DETAILS,
  ROLE_LIST_1,
} from "../config/API_Collection";

// ✅ import your dropdown
import SearchableDropdown from "../components/DropdownSearch";

/* ----------------------------- Helper layouts ----------------------------- */
function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="project-list-container">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      
      </div>
    </div>
  );
}

function RestrictedLayout({ homePath = "projects" }) {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath={homePath} />
        </div>
      
      </div>
    </div>
  );
}

/* ----------------------- Reusable SearchableSelect (filters) ----------------------- */
function SearchableSelect({ label, options, value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hoverIndex, setHoverIndex] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.toLowerCase().includes(term));
  }, [options, q]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else {
      setQ("");
      setHoverIndex(-1);
    }
  }, [open]);

  const scrollToHover = () => {
    if (!listRef.current) return;
    const nodes = listRef.current.querySelectorAll('[role="option"]');
    const idx = Math.max(0, Math.min(hoverIndex, nodes.length - 1));
    const node = nodes[idx];
    if (node?.scrollIntoView) {
      node.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (["ArrowDown", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIndex((i) => Math.min(i + 1, filtered.length - 1));
      scrollToHover();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIndex((i) => Math.max(i - 1, 0));
      scrollToHover();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[hoverIndex];
      if (typeof pick === "string") {
        onChange(pick);
        setOpen(false);
      }
    }
  };

  const currentLabel = value === "All" ? label : value;

  return (
    <div
      ref={wrapRef}
      className={`position-relative ${className}`}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="btn bg-white border rounded-2 w-100 text-start d-flex align-items-center justify-content-between"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="text-truncate">{currentLabel}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
          className={`ms-2 ${open ? "rotate-180" : ""}`}
        >
          <path
            fill="currentColor"
            d="M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708L8.354 10.354a.5.5 0 0 1-.708 0L4.646 7.354a.5.5 0 0 1 0-.708z"
          />
        </svg>
      </button>

      {open && (
        <div
          className="shadow-sm border bg-white rounded-2 mt-1 p-2"
          style={{
            position: "absolute",
            zIndex: 1051,
            minWidth: "100%",
            width: "100%",
          }}
        >
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder="Search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setHoverIndex(0);
            }}
          />

          <div
            role="option"
            tabIndex={-1}
            className={`px-2 py-1 rounded-1 mb-1 ${
              value === "All" ? "bg-primary text-white" : "option-hover"
            }`}
            onMouseEnter={() => setHoverIndex(-1)}
            onClick={() => {
              onChange("All");
              setOpen(false);
            }}
          >
            {label}
          </div>

          <div
            ref={listRef}
            role="listbox"
            style={{ maxHeight: 220, overflowY: "auto" }}
          >
            {filtered.length ? (
              filtered.map((opt, i) => {
                const isActive = opt === value;
                const isHover = i === hoverIndex;
                return (
                  <div
                    key={opt}
                    role="option"
                    tabIndex={-1}
                    className={`px-2 py-1 rounded-1 mb-1 ${
                      isActive
                        ? "bg-primary text-white"
                        : isHover
                        ? "bg-light"
                        : "option-hover"
                    }`}
                    onMouseEnter={() => setHoverIndex(i)}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    {opt}
                  </div>
                );
              })
            ) : (
              <div className="text-muted small px-2 py-1">No matches</div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .option-hover { cursor: pointer; }
        .option-hover:hover { background: #f6f7f9; }
        .rotate-180 { transform: rotate(180deg); transition: transform .12s; }
      `}</style>
    </div>
  );
}

/* --------------------------------- Screen --------------------------------- */
export default function EmployeeList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ---------- AUTH / PERMISSIONS ----------
  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null;
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canListEmployees = actions?.employees?.employee_list === true;
  const canAddEmployee = actions?.employees?.add_employee === true;

  // ---------- PAGE DATA ----------
  const employees = useSelector((s) => s?.employees?.employees) || [];
  const isLoading = useSelector((s) => s?.employees?.isLoading);

  // Debug logging
  useEffect(() => {
    console.log('EmployeeList - isLoading:', isLoading, 'employees count:', employees.length);
  }, [isLoading, employees]);

  // ---------- UI STATE ----------
  const [toast, setToast] = useState({ message: "", type: "" });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // Filters
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100); // Default 100 employees per page
  const [roleLists, setRoleLists] = useState([]);

  const [showModalAdd, setShowModalAdd] = useState(false);
  const handleOpenModalAdd = () => setShowModalAdd(true);
  const handleCloseModalAdd = () => setShowModalAdd(false);

  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);

  const showToast = (message, type) => setToast({ message, type });

  const inputWidth = { width: "280px", maxWidth: "100%" };

  // ---------- FETCH ----------
  const fetchJobRole = async () => {
    try {
      const response = await Axios.get(ROLE_LIST_1);
      setRoleLists(response?.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (canListEmployees) {
      dispatch({ type: FETCH_EMPLOYEES_REQUEST, payload: null });
      fetchJobRole();
    }
  }, [dispatch, canListEmployees]);

  // Role options for filters from API or fallback
  const roleOptions = useMemo(() => {
    const api = roleLists?.data || [];
    if (Array.isArray(api) && api.length > 0) {
      return api
        .map((r) => r?.role_name)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort((a, b) => a.localeCompare(b));
    }
    const names = [
      ...new Set(employees.map((e) => e?.role_name).filter(Boolean)),
    ];
    return names.sort((a, b) => a.localeCompare(b));
  }, [roleLists, employees]);

  // Status helper
  const statusText = (n) =>
    n === 1 ? "Active" : n === 0 ? "Pending" : n === 3 ? "Inactive" : "";

  // ---------- FILTER + PAGINATION ----------
  const filteredEmployees = useMemo(() => {
    const term = (searchTerm || "").toLowerCase();

    return employees.filter((e) => {
      const fullName = `${e?.first_name || ""} ${e?.last_name || ""}`
        .trim()
        .toLowerCase();
      const email = (e?.email || "").toLowerCase();
      const phone = (e?.phone || "").toLowerCase();
      const idRaw = String(e?.employee_id ?? "");
      const idPretty = `emp${idRaw.padStart(3, "0")}`.toLowerCase();

      const matchesSearch =
        !term ||
        fullName.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        idRaw.toLowerCase().includes(term) ||
        idPretty.includes(term);

      const matchesRole =
        selectedRole === "All" ||
        (e?.role_name || "").toLowerCase() === selectedRole.toLowerCase();

      const matchesStatus =
        selectedStatus === "All" || statusText(e?.status) === selectedStatus;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [employees, searchTerm, selectedRole, selectedStatus]);

  const totalPages =
    Math.ceil((filteredEmployees.length || 0) / rowsPerPage) || 1;
  const indexOfLast = currentPage * rowsPerPage;
  const indexOfFirst = indexOfLast - rowsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Build a condensed page list: show `1 2 3 ... last` when there are many pages
  const buildPageButtons = (current, total) => {
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);

    // Keep original behaviour for first two pages
    if (current <= 2) return [1, 2, '...', total];

    // If we're near the end, show the last three pages
    if (current >= total - 1) {
      const start = Math.max(1, total - 2);
      return Array.from({ length: total - start + 1 }, (_, i) => start + i);
    }

    // Default: show current as first, next page, ellipsis, last
    return [current, current + 1, '...', total];
  };
  const pageButtons = buildPageButtons(currentPage, totalPages);

  const employeeDetail = (employeeId) =>
    navigate(`${EMPLOYEE_DETAILS}${employeeId}`);

  // ---------- Add Employee form: dropdown state + maps ----------
  const roleNameOptions = useMemo(
    () =>
      (roleLists?.data || [])
        .map((r) => r?.role_name)
        .filter(Boolean),
    [roleLists]
  );
  const roleMapByName = useMemo(() => {
    const map = {};
    (roleLists?.data || []).forEach((r) => {
      if (r?.role_name != null) map[r.role_name] = String(r.role_id);
    });
    return map;
  }, [roleLists]);

  const jobTitleOptions = useMemo(() => jobTitlesData.jobTitles || [], []);

 
  const billableStatusOptions = useMemo(() => ["Billable", "Non-billable"], []);

  const officeOptions = useMemo(
    () => [
      "Hamilton (Head Office)",
      "Rotorua Office",
      "Thames Office",
      "Wellington Office",
      "Auckland Office",
      "Napier Office",
      "Whitianga Office",
      "Asia Office- Sri Lanka",
      "Whakatāne Office",
      "Cambridge",
      "Raglan",
      "Palmerston North",
      "Christchurch",
      "New Plymouth",
    ],
    []
  );

  // local state for the 4 dropdowns in modal
  const [roleLabel, setRoleLabel] = useState("");
  const [roleIdHidden, setRoleIdHidden] = useState("");

  const [jobTitleLabel, setJobTitleLabel] = useState("");
  const [jobTitleHidden, setJobTitleHidden] = useState("");

 

  const [billableStatusLabel, setBillableStatusLabel] = useState("");
  const [billableStatusHidden, setBillableStatusHidden] = useState("");


  const [officeLabelModal, setOfficeLabelModal] = useState("");
  const [officeHidden, setOfficeHidden] = useState("");

  // handlers
  const onPickRole = (label) => {
    setRoleLabel(label);
    setRoleIdHidden(roleMapByName[label] || ""); // backend expects role_id
  };
  const onPickJobTitle = (label) => {
    setJobTitleLabel(label);
    setJobTitleHidden(label || "");
  };


  const onPickBillableStatus = (label) => {
    setBillableStatusLabel(label);
    setBillableStatusHidden(label || "");
  };
  const onPickOffice = (label) => {
    setOfficeLabelModal(label);
    setOfficeHidden(label || "");
  };

  // ---------- ADD EMPLOYEE ----------
  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (isSubmittingAdd) return;

    try {
      const formData = e.target;
      const payload = {
        first_name: formData.first_name.value,
        last_name: formData.last_name.value,
        email: formData.email.value,
        phone: formData.phone.value,
        role_id: parseInt(formData.role.value, 10),
        job_title: formData.job_title.value,
        billable: formData.billable_status.value === "Billable" ? 1 : 2,
        billable_rate: formData.billable_rate.value,
        office: formData.office.value,
      };

      const required = [
        "first_name",
        "last_name",
        "email",
        "phone",
        "role_id",
        "job_title",
        "billable",
        "billable_rate",
        "office",
      ];

      const errors = validateForm(payload, required);
      setFormErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setIsSubmittingAdd(true);

      const response = await Axios.post(ADD_EMPLOYEE, payload);
      if (response.status === 200) {
        showToast("Employee Added Successfully!", "success");
        handleCloseModalAdd();
        dispatch({ type: FETCH_EMPLOYEES_REQUEST, payload: null });
        return;
      }

      showToast(response?.data?.message || "Failed to add employee", "failed");
      setIsSubmittingAdd(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      setIsSubmittingAdd(false);
    }
  };

  // ---------- RENDER ----------
  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canListEmployees) return <RestrictedLayout homePath="projects" />;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {isLoading && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} />

        <div className="project-list-container">
          {/* Filters Row - Only show when there are employees */}
          {!isLoading && employees.length > 0 && (
            <div className="row align-items-center mb-3 justify-content-start">
              <div className="col-12 col-sm-6 col-lg-3">
                <input
                  type="text"
                  className="form-control"
                  style={inputWidth}
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-2">
                <SearchableSelect
                  label="All Roles"
                  options={roleOptions}
                  value={selectedRole}
                  onChange={(v) => {
                    setSelectedRole(v);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-12 col-sm-6 col-lg-2">
                <SearchableSelect
                  label="All Status"
                  options={["Active", "Pending", "Inactive"]}
                  value={selectedStatus}
                  onChange={(v) => {
                    setSelectedStatus(v);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-12 col-lg-5 d-flex justify-content-end">
                {canAddEmployee && (
                  <button
                    className="btn-sm p-2 btn-primary d-flex align-items-center justify-content-center"
                    onClick={handleOpenModalAdd}
                  >
                    <i className="bi bi-plus-circle me-1" />
                    Add Employee
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Add Employee button for empty state */}
          {!isLoading && employees.length === 0 && canAddEmployee && (
            <div className="row mb-3">
              <div className="col-12 d-flex justify-content-end">
                <button
                  className="btn-sm p-2 btn-primary d-flex align-items-center justify-content-center"
                  onClick={handleOpenModalAdd}
                >
                  <i className="bi bi-plus-circle me-1" />
                  Add Employee
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="loading-wrapper">
              <div className="spinner-border text-secondary" role="status" />
            </div>
          ) : filteredEmployees.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-bordered custom-table">
                  <thead>
                    <tr>
                      <th>Emp ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Job Title</th>
                      <th>Billable Status</th>
                      <th>Status</th>
                      <th>Registered Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEmployees.map((employee) => (
                      <tr
                        key={employee.employee_id}
                        onClick={() => employeeDetail(employee.employee_id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          EMP{String(employee.employee_id).padStart(3, "0")}
                        </td>
                        <td>
                          {employee.first_name} {employee.last_name}
                        </td>
                        <td>{employee.email}</td>
                        <td>{employee.phone}</td>
                        <td>{employee.role_name}</td>
                        <td>{employee.job_title}</td>
                        <td>{employee.billable}</td>
                        <td>{statusText(employee.status)}</td>
                        <td>{employee.created_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Rows per page selector - bottom right */}
              <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Show</span>
                <select
                  className="form-select"
                  style={{ width: '100px', fontSize: '13px' }}
                  value={rowsPerPage === filteredEmployees.length && filteredEmployees.length > 500 ? "all" : rowsPerPage}
                  onChange={(e) => {
                    if (e.target.value === "all") {
                      setRowsPerPage(filteredEmployees.length);
                    } else {
                      setRowsPerPage(Number(e.target.value));
                    }
                    setCurrentPage(1); // Reset to first page
                  }}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="all">All</option>
                </select>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <div className="text-center">
                <i className="bi bi-people" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
                <h5 className="text-muted">No employees available</h5>
                <p className="text-muted small">There are currently no employees in the system.</p>
              </div>
            </div>
          )}

          {/* Add Employee Modal */}
          {showModalAdd &&
            (canAddEmployee ? (
              <div className="custom-project-modal">
                <div className="modal-backdrop fade show" />
                <div
                  className="modal fade show"
                  tabIndex="-1"
                  aria-labelledby="addEmployeeLabel"
                  aria-hidden="true"
                  style={{ display: "block", zIndex: 1050 }}
                >
                  <div
                    className="modal-dialog modal-lg mx-auto"
                    style={{ marginTop: "2%" }}
                  >
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title" id="addEmployeeLabel">
                          Add New Employee
                        </h5>
                      </div>
                      <div className="modal-body">
                        <div className="add-budget-container">
                          <form
                            className="budget-form"
                            onSubmit={handleAddEmployee}
                          >
                            <fieldset
                              disabled={isSubmittingAdd}
                              style={{ border: 0, padding: 0, margin: 0 }}
                            >
                              <div className="form-row">
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.first_name ? "has-error" : ""
                                  }`}
                                >
                                  <label>First Name *</label>
                                  <input type="text" name="first_name" />
                                </div>
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.last_name ? "has-error" : ""
                                  }`}
                                >
                                  <label>Last Name *</label>
                                  <input type="text" name="last_name" />
                                </div>
                              </div>

                              <div className="form-row">
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.email ? "has-error" : ""
                                  }`}
                                >
                                  <label>Email *</label>
                                  <input type="email" name="email" />
                                </div>
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.phone ? "has-error" : ""
                                  }`}
                                >
                                  <label>Phone *</label>
                                  <input
                                    type="text"
                                    name="phone"
                                    minLength={9}
                                    maxLength={12}
                                  />
                                </div>
                              </div>

                              <div className="form-row">
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.role ? "has-error" : ""
                                  }`}
                                >
                                  <label>Job Role *</label>
                                  <SearchableDropdown
                                    label="Select Role"
                                    allowAll={false}
                                    options={roleNameOptions}
                                    value={roleLabel}
                                    onChange={onPickRole}
                                  />
                                  {/* backend expects role_id via "role" */}
                                  <input type="hidden" name="role" value={roleIdHidden} />
                                </div>
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.job_title ? "has-error" : ""
                                  }`}
                                >
                                  <label>Job Title *</label>
                                  <SearchableDropdown
                                    label="Select Job Title"
                                    allowAll={false}
                                    options={jobTitleOptions}
                                    value={jobTitleLabel}
                                    onChange={onPickJobTitle}
                                  />
                                  <input
                                    type="hidden"
                                    name="job_title"
                                    value={jobTitleHidden}
                                  />
                                </div>
                              </div>

                              <div className="form-row">
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.salary_type ? "has-error" : ""
                                  }`}
                                >
                                  <label>Billable Status *</label>
                                  <SearchableDropdown
                                    label="Select Billable Status"
                                    allowAll={false}
                                    options={billableStatusOptions}
                                    value={billableStatusLabel}
                                    onChange={onPickBillableStatus}
                                  />
                                  <input
                                    type="hidden"
                                    name="billable_status"
                                    value={billableStatusHidden}
                                  />
                                </div>
                                <div
                                  className={`form-group flex-item ${
                                    formErrors.salary ? "has-error" : ""
                                  }`}
                                >
                                  <label>Billable Rate *</label>
                                  <input type="text" name="billable_rate" defaultValue={0}/>
                                </div>
                              </div>

                              <div className="form-row">
                                <div className="form-group flex-item">
                                  <label>Location *</label>
                                  <SearchableDropdown
                                    label="Select Location"
                                    allowAll={false}
                                    options={officeOptions}
                                    value={officeLabelModal}
                                    onChange={onPickOffice}
                                  />
                                  <input type="hidden" name="office" value={officeHidden} />
                                </div>
                                <div className="form-group flex-item" />
                              </div>
                            </fieldset>

                            <div className="d-flex justify-content-center">
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmittingAdd}
                              >
                                {isSubmittingAdd ? "Adding..." : "Add Employee"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary ms-2"
                                onClick={handleCloseModalAdd}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <AccessRestricted homePath="projects" />
            ))}
        </div>

      
      </div>
    </div>
  );
}
